import { useState } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { useAgents, useDeleteAgent } from '../../hooks/useAgents';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Table, { TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import CreateAgentModal from './CreateAgentModal';
import EditAgentModal from './EditAgentModal';
import { Agent } from '../../services/agents.service';
import DogLoader from '../../components/ui/DogLoader';

export default function AgentsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  const { data: agents, isLoading } = useAgents();
  const deleteAgent = useDeleteAgent();

  const filteredAgents = agents?.filter((agent) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      agent.firstName?.toLowerCase().includes(searchLower) ||
      agent.lastName?.toLowerCase().includes(searchLower) ||
      agent.email?.toLowerCase().includes(searchLower)
    );
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      try {
        await deleteAgent.mutateAsync(id);
      } catch (error) {
        console.error('Error deleting agent:', error);
        alert('Failed to delete agent');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success">Active</Badge>;
      case 'INACTIVE':
        return <Badge variant="error">Inactive</Badge>;
      case 'ON_BREAK':
        return <Badge variant="warning">On Break</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  if (isLoading) {
    return <DogLoader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agents</h1>
          <p className="text-gray-600 mt-1">Manage your contact center agents</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Agent
        </Button>
      </div>

      <Card>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search agents by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {filteredAgents && filteredAgents.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Skills</TableHead>
                <TableHead>Languages</TableHead>
                <TableHead>Timezone</TableHead>
                <TableHead>Utilization Target</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAgents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>
                    <div className="font-medium">
                      {agent.firstName} {agent.lastName}
                    </div>
                  </TableCell>
                  <TableCell>{agent.email}</TableCell>
                  <TableCell>{getStatusBadge(agent.status)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {agent.skills && agent.skills.length > 0 ? (
                        <>
                          {agent.skills.slice(0, 2).map((skill, idx) => (
                            <Badge key={idx} variant="info">
                              {typeof skill === 'string' ? skill : skill.skillGroup?.name || 'Skill'}
                            </Badge>
                          ))}
                          {agent.skills.length > 2 && (
                            <Badge variant="default">+{agent.skills.length - 2}</Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 text-sm">No skills</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {agent.languages && agent.languages.length > 0 ? (
                        agent.languages.map((lang) => (
                          <Badge key={lang} variant="default">
                            {lang}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{agent.timezone}</TableCell>
                  <TableCell>{agent.targetUtilization}%</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedAgent(agent)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(agent.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchQuery ? 'No agents found matching your search.' : 'No agents yet. Create your first agent to get started.'}
            </p>
          </div>
        )}
      </Card>

      <CreateAgentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      {selectedAgent && (
        <EditAgentModal
          agent={selectedAgent}
          isOpen={!!selectedAgent}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </div>
  );
}
